/**
 * @typedef {Object} RailConfig
 * @property {string} width - The width of the rail.
 * @property {function} useGuard - the guard function to determine if the rail should be displayed.
 * @property {boolean} closeOnNavigation - Determines if the rail should be closed when navigating.
 * @property {React.ComponentType} component - The React component to render in the rail.
 * @typedef {Object.<string, RailConfig>} Rails - A mapping of string keys to rail configurations.
 */


import { useFlag } from '@calendly/feature-check-react'
import { withSafeSuspense } from '@calendly/safe-suspense'
import { lazy } from 'react'

import changeSet from 'core/change_set'
import { RemoteCommercePaneMFE } from 'features/commerce'
import { RemoteContactsPane } from 'features/crm'
import { Sidebar as EventTypeEditorSidebar } from 'features/event_type_editor/lazy'
import { useNotetakerEntitled } from 'features/meeting_intelligence'
import { MeetingDetailRightRail } from 'features/meetings/lazy'
import { useIsUserProfileViewable } from 'features/user_profiles/hooks/useIsUserProfileViewable'
import { InvitationSidebar, UserSidebar } from 'features/user_profiles/lazy'
import { isMeetingsTabPathPrefix } from 'hooks/home/tabs'

import { ExampleOne, ExampleTwo } from './lazy'

const defaultRightRailWidth = '440px'

export const lazyImportFallback = () => ({ default: () => null })

const HelpRightRail = withSafeSuspense(
  lazy(() => import('../contextual_help/contextual_help_rail.js')
    .then((m) => ({ default: m.ContextualHelpRightRail }))
    .catch(lazyImportFallback)),
  null
)

const SingleUseLinkSidebar = withSafeSuspense(
  lazy(() => import('../single_use_link_sidebar/sidebar')
    .then((m) => ({ default: m.Sidebar }))
    .catch(lazyImportFallback)),
  null
)

const AiChatRightRail = withSafeSuspense(
  lazy(() => import('features/agent_hub/ai_chat_discovery/AiChatRightRail')
    .then((m) => ({ default: m.AiChatRightRail }))
    .catch(lazyImportFallback)),
  null
)

/**
 * Rails configuration object.
 * @type {Rails}
 */
export const routes = {
  // istanbul ignore next - this is a test route
  example: {
    component: ExampleOne,
    width: defaultRightRailWidth,
    useGuard: (props) => {
      const { isEntitled, isLoading } = useNotetakerEntitled()
      return isEntitled || isLoading || props.variant === 'regular'
    }
  },
  // istanbul ignore next - this is a test route
  example_two: {
    component: ExampleTwo,
    width: defaultRightRailWidth,
    closeOnNavigation: true
  },
  profile: {
    component: UserSidebar,
    width: defaultRightRailWidth,
    closeOnNavigation: true,
    useGuard: useIsUserProfileViewable
  },
  invitation: {
    component: InvitationSidebar,
    width: defaultRightRailWidth,
    closeOnNavigation: true,
    useGuard: useIsUserProfileViewable
  },
  remote_contacts: {
    component: RemoteContactsPane,
    width: defaultRightRailWidth,
    closeOnNavigation: true
  },
  event_type_editor: {
    component: EventTypeEditorSidebar,
    width: defaultRightRailWidth,
    closeOnNavigation: false,
    canBeClosed: () => changeSet.confirmReset()
  },
  single_use_link: {
    component: SingleUseLinkSidebar,
    width: defaultRightRailWidth,
    closeOnNavigation: false,
    canBeClosed: () => changeSet.confirmReset()
  },
  meeting_details: {
    component: MeetingDetailRightRail,
    width: defaultRightRailWidth,
    // Keep open when navigating TO meetings page with selected/share_id (e.g. ViewMeetingButton from scheduling)
    closeOnNavigation: ({ toPath, searchParams }) => (
      !(isMeetingsTabPathPrefix(toPath) && (searchParams.selected || searchParams.share_id))
    )
  },
  contextual_help: {
    component: HelpRightRail,
    width: defaultRightRailWidth,
    closeOnNavigation: true,
    useGuard: () => useFlag('activation_q3')
  },
  commerce: {
    component: RemoteCommercePaneMFE,
    width: defaultRightRailWidth,
    closeOnNavigation: true
  },
  ai_chat: {
    component: AiChatRightRail,
    width: defaultRightRailWidth,
    closeOnNavigation: false,
  }
}
